import { useState, useEffect } from 'react';
import resolveDefaultReminderPlan from '../helpers/adressHelpers/resolveDefaultReminderPlan';
import resolveAdressLabels, { resolveProjectLabels } from '../helpers/adressHelpers/resolveAdressLabels';
import { useFirestore, useFirestoreGeneral } from "../firebase/useFirestore";
import mapLegacyToSubscription from '../helpers/subscriptionHelpers/mapLegacyToSubscription';
import {
  UNREACHABLE_AFTER_DAYS_DEFAULT,
  normalizeUnreachableAfterDays,
} from '../helpers/adressHelpers/unreachableAfterDays';

const useSettings = () => {
  const [loaded, setLoaded] = useState(false);

  // Module-display state (CSS strings) — derived per-field from CompagnyMeta for backwards-compat
  const [MKBAOn, setMKBAOn] = useState('none');
  const [personasOn, setPersonasOn] = useState('none');
  const [housevisits, setHousevisits] = useState(false);
  const [impactAI, setImpactAI] = useState('none');
  const [finance, setFinance] = useState('none');
  const [portfolio, setPortfolio] = useState('none');
  const [impactFunctionality, setImpactFunctionality] = useState(false);
  const [deccosPro, setDeccosPro] = useState(false);

  // Onboarding-flow velden (T8)
  const [centralEmail, setCentralEmail] = useState('');
  const [partner, setPartner] = useState(null);
  const [measurementApproach, setMeasurementApproach] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [selectedSectors, setSelectedSectors] = useState([]);
  // CompagnyMeta.SectorContext — sector-specific context answers, nested per
  // sector slug. See docs/features/sector-context-questions.md
  const [sectorContext, setSectorContext] = useState({});

  // Branding & meta
  const [docid, setDocid] = useState('');
  const [PrimaryColor, setPrimaryColor] = useState('rgb(244, 129, 131)');
  const [SecundairyColor, setSecundairyColor] = useState('#63cadc');
  const [TertairyColor, setTertairyColor] = useState('rgb(255, 203, 15)');
  const [AIInterviewerOn, setAIInterviewerOn] = useState(false);
  const [compagnyName, setCompagnyName] = useState('');
  const [logo, setLogo] = useState('none');
  const [stripeId, setStripeId] = useState('');
  const [email, setEmail] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('');
  const [website, setWebsite] = useState('');
  const [onboarding, setOnboarding] = useState(false);
  const [parents, setParents] = useState([]);
  const [impactPerEuro, setImpactPerEuro] = useState(false);
  const [adjustStatusInTaskEditor, setAdjustStatusInTaskEditor] = useState(false);
  // Weergavelaag over de statuslijsten: eigen labels en verborgen statussen
  // per organisatie. Standaard een lege map — resolveStatusDisplay leest dat
  // als 'geen instelling' en valt terug op de canonieke woorden met alles
  // zichtbaar. Zie docs/decisions.md#adr-032.
  const [adressStatusConfig, setAdressStatusConfig] = useState({});
  // De labels bij huishoudens, zichtbaar en op volgorde (resolveAdressLabels).
  const [adressLabels, setAdressLabels] = useState(() => resolveAdressLabels({}));
  // De labels die deze organisatie op projecten in het portfolio zet (resolveProjectLabels).
  const [projectLabels, setProjectLabels] = useState(() => resolveProjectLabels({}));
  // De soorten afspraak (CompagnyMeta.AppointmentKinds), rauw; useAppointmentKinds
  // legt er het merk overheen.
  const [appointmentKindsConfig, setAppointmentKindsConfig] = useState({});
  // De rekenregels van de facturatie (CompagnyMeta.BillingRules), rauw;
  // resolveBillingRules maakt er regels van, of null als ze niet compleet zijn.
  const [billingRulesConfig, setBillingRulesConfig] = useState({});
  // Diensten bij een wervingsactie (CompagnyMeta.WervingsDiensten), rauw;
  // resolveWervingsDiensten maakt er de lijst en de namen van.
  const [wervingsDienstenConfig, setWervingsDienstenConfig] = useState({});
  const [personaStatusConfig, setPersonaStatusConfig] = useState({});
  // Welke kolommen het huishoudenoverzicht toont, plus eigen gegevens als
  // extra kolom. Leeg = alles aan, geen extra's.
  const [householdColumns, setHouseholdColumns] = useState({});
  const [autoPlanHouseVisitStatus, setAutoPlanHouseVisitStatus] = useState(false);
  const [autoCompleteHouseVisitStatus, setAutoCompleteHouseVisitStatus] = useState(false);
  // Materialenvoorraad mag onder 0 zakken. Default OFF: zonder expliciete
  // instelling blijft de blokkade bij onvoldoende voorraad staan.
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [stickyDeleteColumn, setStickyDeleteColumn] = useState(false);
  const [tocBannerDismissed, setTocBannerDismissed] = useState(false);
  const [wervingsacties, setWervingsacties] = useState(true);
  const [defaultHouseVisitReminder, setDefaultHouseVisitReminder] = useState(false);
  // Kanaal (e-mail/sms/beide) en dagen van tevoren van die herinnering, als één
  // plan. Standaard uit; de helper leest dezelfde velden als de instellingen.
  const [defaultHouseVisitReminderPlan, setDefaultHouseVisitReminderPlan] = useState(() => resolveDefaultReminderPlan({}));
  // Demo-portfolio-accounts: demo-projecten tonen als actief in portfolio-views
  const [showDemoProjects, setShowDemoProjects] = useState(false);
  const [smsRemindersEnabled, setSmsRemindersEnabled] = useState(false);
  const [defaultAgendaView, setDefaultAgendaView] = useState('timeGridWeek');
  // Agenda-instellingen (Instellingen → Ondersteuning → 'Agenda'); gelezen door resolveAgendaOptions.
  const [agendaHideWeekends, setAgendaHideWeekends] = useState(false);
  const [agendaSnapMinutes, setAgendaSnapMinutes] = useState(30);
  const [agendaViews, setAgendaViews] = useState(/** @type {{Hidden?: Record<string, *>}} */ ({}));
  // Ruw doorgegeven; resolveAgendaOptions is de ene plek die de waarde uitlegt.
  const [agendaScheduleDisplay, setAgendaScheduleDisplay] = useState(/** @type {*} */ (undefined));
  // Wachttijd voor 'Onbereikbaar'. Standaard 14 dagen; een ontbrekende waarde
  // mag nooit 0 worden, dat zou de wachttijd stil uitschakelen.
  const [unreachableAfterDays, setUnreachableAfterDays] = useState(
    UNREACHABLE_AFTER_DAYS_DEFAULT
  );

  // Subscription object — canonical billing/module state from Subscriptions-collectie.
  // Falls back to mapLegacyToSubscription(compagnyMeta) when subscription-doc has no modules-field
  // (orgs not yet migrated by T5).
  const [subscription, setSubscription] = useState(null);

  const compagnyMeta = useFirestore('CompagnyMeta');
  const subscriptions = useFirestore('Subscriptions');

  // MSI effects = OutputEffects pulled from the Meetstandaard (Standard === 'true').
  // Used to gate the KPI cards for tenants that work with MSI effects even
  // when the personas module is off.
  const msiEffects = useFirestoreGeneral('OutputEffects', 'Standard', 'true');
  const hasMSIEffects = Array.isArray(msiEffects) && msiEffects.length > 0;

  useEffect(() => {
    if (!compagnyMeta || compagnyMeta.length === 0) return;

    compagnyMeta.forEach(item => {
      item.MKBA === 'true' ? setMKBAOn('block') : setMKBAOn('none');
      // Personas/Portfolio/ImpactFunctionality werden historisch als string
      // ('true'/'false') geschreven; onboarding-flow (createProjectAccount) schrijft
      // nu booleans afgeleid uit `modules`. Accepteer beide vormen — mirror van
      // de HouseVisits-aanpak hieronder.
      (item.Personas === true || item.Personas === 'true') ? setPersonasOn('flex') : setPersonasOn('none');
      item.PrimaryColor && setPrimaryColor(item.PrimaryColor);
      item.SecundairyColor && setSecundairyColor(item.SecundairyColor);
      item.TertairyColor && setTertairyColor(item.TertairyColor);
      item.ImpactAI === 'true' ? setImpactAI('block') : setImpactAI('none');
      item.CommunityName && setCompagnyName(item.CommunityName);
      item.Logo && setLogo(item.Logo);
      item.StripeCustomerId && setStripeId(item.StripeCustomerId);
      item.Email && setEmail(item.Email);
      item.SubscriptionType && setSubscriptionType(item.SubscriptionType);
      item.Financing === true ? setFinance('flex') : setFinance('none');
      (item.Portfolio === true || item.Portfolio === 'true') ? setPortfolio('block') : setPortfolio('none');
      item.Website && setWebsite(item.Website);
      item.AIinterviewer && setAIInterviewerOn(true);
      item.Parent && setParents(item.Parent);
      (item.HouseVisits === true || item.HouseVisits === 'true') ? setHousevisits(true) : setHousevisits(false);
      (item.AdjustStatusInTaskEditor === true || item.AdjustStatusInTaskEditor === 'true') ? setAdjustStatusInTaskEditor(true) : setAdjustStatusInTaskEditor(false);
      (item.TOCBannerDismissed === true || item.TOCBannerDismissed === 'true') ? setTocBannerDismissed(true) : setTocBannerDismissed(false);
      // Default OFF: only on when explicitly enabled. ToggleButton writes strings, hence the double compare.
      (item.AutoPlanHouseVisitStatus === true || item.AutoPlanHouseVisitStatus === 'true') ? setAutoPlanHouseVisitStatus(true) : setAutoPlanHouseVisitStatus(false);
      (item.AutoCompleteHouseVisitStatus === true || item.AutoCompleteHouseVisitStatus === 'true') ? setAutoCompleteHouseVisitStatus(true) : setAutoCompleteHouseVisitStatus(false);
      // Default OFF: zonder instelling blijft de voorraad geblokkeerd op 0.
      (item.AllowNegativeStock === true || item.AllowNegativeStock === 'true') ? setAllowNegativeStock(true) : setAllowNegativeStock(false);
      // Default OFF: de meeste organisaties hebben zo weinig kolommen aanstaan
      // dat de verwijderknop gewoon in beeld blijft.
      (item.StickyDeleteColumn === true || item.StickyDeleteColumn === 'true') ? setStickyDeleteColumn(true) : setStickyDeleteColumn(false);
      // Default OFF: only on when explicitly enabled.
      (item.DefaultHouseVisitReminder === true || item.DefaultHouseVisitReminder === 'true') ? setDefaultHouseVisitReminder(true) : setDefaultHouseVisitReminder(false);
      setDefaultHouseVisitReminderPlan(resolveDefaultReminderPlan(item));
      // Default OFF: SMS reminders are billed, so only on when explicitly enabled.
      (item.SmsRemindersEnabled === true || item.SmsRemindersEnabled === 'true') ? setSmsRemindersEnabled(true) : setSmsRemindersEnabled(false);
      item.DefaultAgendaView && setDefaultAgendaView(item.DefaultAgendaView);
      // Default OFF: weekend zichtbaar tenzij expliciet verborgen. ToggleButton schrijft tekst.
      setAgendaHideWeekends(item.AgendaHideWeekends === true || item.AgendaHideWeekends === 'true');
      setAgendaSnapMinutes(Number(item.AgendaSnapMinutes) === 15 ? 15 : 30);
      setAgendaViews(item.AgendaViews && typeof item.AgendaViews === 'object' ? item.AgendaViews : {});
      setAgendaScheduleDisplay(item.AgendaScheduleDisplay);
      setUnreachableAfterDays(normalizeUnreachableAfterDays(item.UnreachableAfterDays));
      // Default ON: only off when explicitly disabled.
      (item.Wervingsacties === false || item.Wervingsacties === 'false') ? setWervingsacties(false) : setWervingsacties(true);

      setImpactFunctionality(item.ImpactFunctionality === true || item.ImpactFunctionality === 'true');
      // Deccos Pro (researcher tier) — gates the Correlaties dashboard card.
      // Flag-only for now; wiring into Stripe modules happens when pricing lands.
      setDeccosPro(item.DeccosPro === true || item.DeccosPro === 'true');
      setImpactPerEuro(item.ImpactPerEuro === true || item.ImpactPerEuro === 'true');
      setShowDemoProjects(item.ShowDemoProjects === true || item.ShowDemoProjects === 'true');

      setOnboarding(item.Onboarding);
      setDocid(item.docid);

      // Onboarding-flow velden (T8)
      item.CentralEmail !== undefined && setCentralEmail(item.CentralEmail);
      item.Partner !== undefined && setPartner(item.Partner);
      item.MeasurementApproach !== undefined && setMeasurementApproach(item.MeasurementApproach);
      item.OnboardingStatus !== undefined && setOnboardingStatus(item.OnboardingStatus);
      Array.isArray(item.SelectedSectors) && setSelectedSectors(item.SelectedSectors);
      // Unconditional, unlike its neighbours: an absent SectorContext genuinely
      // means "no answers yet", so the `!== undefined` guard would leave a stale
      // map behind after the last answer is cleared.
      setSectorContext(item.SectorContext || {});

      // Onvoorwaardelijk, net als SectorContext: een ontbrekende map betekent
      // echt 'niets ingesteld', dus een `!== undefined`-guard zou na het
      // wissen van de laatste instelling een oude map laten staan.
      setAdressStatusConfig(item.AdressStatusConfig || {});
      setAdressLabels(resolveAdressLabels(item));
      setProjectLabels(resolveProjectLabels(item));
      setPersonaStatusConfig(item.PersonaStatusConfig || {});
      setAppointmentKindsConfig(item.AppointmentKinds || {});
      setBillingRulesConfig(item.BillingRules || {});
      setWervingsDienstenConfig(item.WervingsDiensten || {});
      setHouseholdColumns(item.HouseholdColumns || {});
    });

    setLoaded(true);
  }, [compagnyMeta]);

  useEffect(() => {
    if (!compagnyMeta || compagnyMeta.length === 0) return;

    const compagnyMetaItem = compagnyMeta[0];
    const subscriptionDoc = (subscriptions && subscriptions.length > 0) ? subscriptions[0] : null;

    const base = (subscriptionDoc && subscriptionDoc.modules)
      ? subscriptionDoc
      : mapLegacyToSubscription(compagnyMetaItem);

    // De superadmin-velden leven op het Subscriptions-doc, óók voor
    // factuurklanten die verder via mapLegacyToSubscription worden afgeleid.
    // Layer ze er hier overheen zodat ze ongeacht het billing-type doorkomen.
    //
    // Waarom dit voor alle drie moet en niet alleen voor endDate:
    // setSubscriptionInvoice en setSubscriptionEndDate maken bij een klant
    // zónder document een MINIMAAL document aan, bewust zonder `modules`. Dan
    // valt `base` terug op de legacy-mapping, die deze velden niet kent, en zou
    // een zojuist opgeslagen bedrag leeg terugkomen in het formulier — waarna
    // de volgende opslag het stil op null zet en die organisatie uit de MRR valt.
    const effectiveSubscription = {
      ...base,
      endDate: subscriptionDoc?.endDate ?? base.endDate ?? null,
      InvoiceAmountCents: subscriptionDoc?.InvoiceAmountCents ?? null,
      InvoicePeriodStart: subscriptionDoc?.InvoicePeriodStart ?? null,
    };

    setSubscription(effectiveSubscription);
  }, [compagnyMeta, subscriptions]);

  return {
    loaded,
    MKBAOn,
    personasOn,
    docid,
    PrimaryColor,
    SecundairyColor,
    TertairyColor,
    impactAI,
    compagnyName,
    logo,
    subscription,
    stripeId,
    email,
    subscriptionType,
    finance,
    portfolio,
    website,
    onboarding,
    AIInterviewerOn,
    parents,
    housevisits,
    impactFunctionality,
    deccosPro,
    hasMSIEffects,
    impactPerEuro,
    adjustStatusInTaskEditor,
    autoPlanHouseVisitStatus,
    autoCompleteHouseVisitStatus,
    allowNegativeStock,
    defaultHouseVisitReminder,
    defaultHouseVisitReminderPlan,
    smsRemindersEnabled,
    defaultAgendaView,
    agendaHideWeekends,
    agendaSnapMinutes,
    agendaViews,
    agendaScheduleDisplay,
    unreachableAfterDays,
    adressStatusConfig,
    adressLabels,
    projectLabels,
    personaStatusConfig,
    appointmentKindsConfig,
    billingRulesConfig,
    wervingsDienstenConfig,
    householdColumns,
    stickyDeleteColumn,
    tocBannerDismissed,
    wervingsacties,
    showDemoProjects,
    // Onboarding-flow velden (T8)
    centralEmail,
    partner,
    measurementApproach,
    onboardingStatus,
    selectedSectors,
    sectorContext,
  };
};

export default useSettings;
